import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LandingLayout from "./LandingLayout";

const HowItWorksPage = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll(".article-section, .earning-card");
      const scrollPosition = window.scrollY + 200;

      sections.forEach((section) => {
        const id = section.getAttribute("id");
        if (id) {
          const top = section.offsetTop;
          const height = section.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(id);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <LandingLayout title="How It Works | Rebel">
      <section className="page-hero howit-hero">
        <div className="hero-glow"></div>
        <div className="hero-copy">
          <div className="breadcrumbs">
            <Link to="/">Home</Link>
            <span>›</span>
            <span>How It Works</span>
          </div>
          <p className="updated">Updated 4 months ago</p>
          <h1>How It Works</h1>
          <p>Everything you need to know about earning, growing, and getting paid on Rebel.</p>
        </div>
        <div className="hero-art">
          <img src="/assets/computer.png" alt="Rebel creator dashboard displayed on laptop and phone" />
        </div>
      </section>

      <div className="page-shell">
        <aside className="guide-sidebar">
          <div className="guide-card sticky">
            <h2>In This Guide</h2>
            <nav className="guide-nav">
              <a className={activeSection === "overview" ? "selected" : ""} href="#overview">
                <img src="/assets/icons/overview.svg" alt="" />Overview
              </a>
              <a className={activeSection === "earn" ? "selected" : ""} href="#earn">
                <img src="/assets/icons/earn.svg" alt="" />How Creators Earn
              </a>
              <a className={activeSection === "subscription" ? "selected" : ""} href="#subscription">
                <img src="/assets/icons/premium.svg" alt="" />Subscription Model
              </a>
              <a className={activeSection === "advertising" ? "selected" : ""} href="#advertising">
                <img src="/assets/icons/ads.svg" alt="" />Ad Revenue (PPV)
              </a>
              <a className={activeSection === "fundme" ? "selected" : ""} href="#fundme">
                <img src="/assets/icons/heart.svg" alt="" />FundMe / Donations
              </a>
              <a className={activeSection === "getting-started" ? "selected" : ""} href="#getting-started">
                <img src="/assets/icons/profile.svg" alt="" />Getting Started
              </a>
              <a className={activeSection === "payouts" ? "selected" : ""} href="#payouts">
                <img src="/assets/icons/wallet.svg" alt="" />Payouts
              </a>
              <a className={activeSection === "subscriber-setup" ? "selected" : ""} href="#subscriber-setup">
                <img src="/assets/icons/viewer.svg" alt="" />Subscriber Setup
              </a>
              <a className={activeSection === "creator-setup" ? "selected" : ""} href="#creator-setup">
                <img src="/assets/icons/creator.svg" alt="" />Creator Setup
              </a>
            </nav>


            <div className="support-card">
              <h3>Still have questions?</h3>
              <p>Our support team is here to help.</p>
              <Link className="button button-support" to="/creator">
                Contact Support
              </Link>
            </div>
          </div>
        </aside>

        <article className="article-content">
          <section className="article-section" id="overview">
            <span className="section-kicker">Overview</span>
            <h2>How Does Rebel Work?</h2>
            <p>Rebel is a creator monetization platform that allows you to earn money from your content — not just views, but real revenue.</p>
            <p>Creators upload videos, articles, photos, live streams and more. Then choose how they want to monetize:</p>

            <div className="model-summary">
              <div>
                <img src="/assets/icons/premium.svg" alt="" />
                <strong>Subscription-Based</strong>
                <span>Premium Model</span>
              </div>
              <div>
                <img src="/assets/icons/ads.svg" alt="" />
                <strong>Ad Revenue</strong>
                <span>Paid Per View Model</span>
              </div>
              <div>
                <img src="/assets/icons/heart.svg" alt="" />
                <strong>Funding / Donations</strong>
                <span>Direct fan support</span>
              </div>
            </div>
            <p>You decide how you build your audience and income.</p>
          </section>

          <aside className="mission-callout">
            <img src="/assets/icons/overview.svg" alt="" />
            <h3>Our Mission</h3>
            <p>Empowering creators across The Bahamas and beyond to turn their passion into sustainable income.</p>
          </aside>

          <section className="article-section wide-section" id="earn">
            <span className="section-kicker">Monetization</span>
            <h2>How Creators Earn on Rebel</h2>
            <p>Rebel offers two primary monetization models, plus direct support through FundMe donations.</p>
          </section>

          <section className="earning-card" id="subscription">
            <div className="earning-illustration">
              <img src="/assets/illustrations/subscription-model.svg" alt="Premium subscription illustration" />
            </div>
            <div className="earning-copy">
              <span className="number">1</span>
              <div>
                <h3>Subscription Model (Premium)</h3>
                <ul className="check-list">
                  <li>Set a monthly subscription price between <strong>$5 – $50</strong>.</li>
                  <li>Subscribers pay monthly to access your exclusive content.</li>
                </ul>
                <h4>Revenue Split</h4>
                <p>Creators receive <strong className="accent-text">80%</strong> of subscription revenue. Rebel retains 20% to cover hosting, payment processing, infrastructure, and support.</p>
                <div className="important-box">
                  <strong>Important</strong>
                  <ul>
                    <li>No ads will run on subscription content.</li>
                    <li>Your page remains premium and ad-free.</li>
                    <li>Best for exclusive or loyal fan-based creators.</li>
                  </ul>
                </div>
              </div>
            </div>
            <aside className="audience-box">
              <img src="/assets/icons/viewer.svg" alt="" />
              <h4>Who It’s For</h4>
              <p>Ideal for creators with loyal audiences and exclusive content.</p>
            </aside>
          </section>

          <section className="earning-card" id="advertising">
            <div className="earning-illustration">
              <img src="/assets/illustrations/ad-revenue-model.svg" alt="Paid per view advertising illustration" />
            </div>
            <div className="earning-copy">
              <span className="number pink-number">2</span>
              <div>
                <h3>Ad Revenue Model (Paid Per View)</h3>
                <p>If your account is set to Free, your videos may qualify for pre-roll ads. Rebel runs a short advertisement before your video.</p>
                <p>Creators earn a share of advertising revenue generated from ads shown before their videos.</p>
                <h4>Revenue Split</h4>
                <div className="split-grid">
                  <div><strong>60%</strong><span>To Creator</span></div>
                  <div><strong>40%</strong><span>To Rebel</span></div>
                </div>
                <h4>Estimated Earnings</h4>
                <p>Creator earnings vary depending on advertiser demand, but creators may earn approximately <strong className="accent-text">$4–$6 per 1,000 ad views</strong>.</p>
                <div className="important-box">
                  <strong>Important</strong>
                  <ul>
                    <li>Only Free accounts are eligible.</li>
                    <li>Subscription accounts do not run ads.</li>
                    <li>Ideal for high-volume and public-facing content.</li>
                  </ul>
                </div>
              </div>
            </div>
            <aside className="audience-box">
              <img src="/assets/icons/ads.svg" alt="" />
              <h4>Who It’s For</h4>
              <p>Great for creators with high traffic and public content.</p>
            </aside>
          </section>

          <section className="earning-card compact-card" id="fundme">
            <div className="earning-illustration">
              <img src="/assets/illustrations/fundme.svg" alt="FundMe donation illustration" />
            </div>
            <div className="earning-copy">
              <span className="number">3</span>
              <div>
                <h3>FundMe / Donations</h3>
                <p>Creators can receive direct support from followers through the FundMe feature.</p>
                <p>This works on both Free and Subscription accounts.</p>
              </div>
            </div>
          </section>

          <section className="article-section wide-section" id="getting-started">
            <span className="section-kicker">Join Rebel</span>
            <h2>Getting Started on Rebel</h2>
            <p>You can join Rebel as either a <strong>Subscriber</strong> or a <strong>Content Creator</strong>.</p>
            <div className="step-grid four">
              <div className="mini-step"><span>1</span><img src="/assets/icons/id.svg" alt="" /><h3>Verify Identity</h3><p>Upload the required verification documents.</p></div>
              <div className="mini-step"><span>2</span><img src="/assets/icons/bank.svg" alt="" /><h3>Add Bank</h3><p>Add the bank account that will receive payouts.</p></div>
              <div className="mini-step"><span>3</span><img src="/assets/icons/earn.svg" alt="" /><h3>Choose Model</h3><p>Select Subscription or Free Ad Revenue.</p></div>
              <div className="mini-step"><span>4</span><img src="/assets/icons/creator.svg" alt="" /><h3>Upload Content</h3><p>Start publishing and building your audience.</p></div>
            </div>
          </section>

          <section className="article-section payout-section" id="payouts">
            <div>
              <span className="section-kicker">Your Money</span>
              <h2>Payout Information</h2>
              <div className="payout-metrics">
                <div><span>Minimum Withdrawal</span><strong>$10</strong></div>
                <div><span>Earnings Availability</span><strong>7-Day Rolling</strong></div>
                <div><span>Bank Arrival</span><strong>3–5 Business Days</strong></div>
              </div>
              <p>Withdrawals are processed immediately. Your wallet displays Total Balance, Current Balance, and Earnings.</p>
            </div>
            <img className="wallet-art" src="/assets/icons/wallet.svg" alt="" />
          </section>

          <section className="article-section setup-section wide-section" id="subscriber-setup">
            <span className="section-kicker">For Viewers</span>
            <h2>Setup Steps as a Rebel Subscriber</h2>
            <ol className="numbered-steps">
              <li><span>1</span><div><strong>Sign Up</strong><p>Go to <strong>www.rebeltv.me</strong> and create your account.</p></div></li>
              <li><span>2</span><div><strong>Select the Profile Icon</strong><p>Open your profile menu after signing up.</p></div></li>
              <li><span>3</span><div><strong>Select My Profile</strong><p>Go to your profile page.</p></div></li>
              <li><span>4</span><div><strong>Select Edit Profile</strong><p>Open the profile editing screen.</p></div></li>
              <li><span>5</span><div><strong>Upload Profile Image</strong><p>A profile photo is required for monetization.</p></div></li>
              <li><span>6</span><div><strong>Upload Cover Image</strong><p>Add a banner or logo. This is required for monetization.</p></div></li>
              <li><span>7</span><div><strong>Add a Card</strong><p>Optional, but required to subscribe to creators with paid subscriptions.</p></div></li>
            </ol>
          </section>

          <section className="article-section setup-section wide-section" id="creator-setup">
            <span className="section-kicker">For Creators</span>
            <h2>Setup Steps as a Content Creator</h2>
            <p>To monetize your content, you must complete KYC verification.</p>

            <div className="creator-setup-grid">
              <article>
                <span className="setup-number">Step 1</span>
                <img src="/assets/icons/id.svg" alt="" />
                <h3>Upload Verification Documents</h3>
                <ul>
                  <li>Select the Profile Icon.</li>
                  <li>Select Documents.</li>
                  <li>Upload your government-issued ID.</li>
                  <li>Wait for approval, usually within 24 hours.</li>
                </ul>
              </article>

              <article>
                <span className="setup-number">Step 2</span>
                <img src="/assets/icons/bank.svg" alt="" />
                <h3>Add Bank Account</h3>
                <ul>
                  <li>Select Add Bank.</li>
                  <li>Enter your banking information.</li>
                  <li>Ensure it matches your verified ID country.</li>
                </ul>
              </article>

              <article>
                <span className="setup-number">Step 3</span>
                <img src="/assets/icons/earn.svg" alt="" />
                <h3>Choose Your Monetization Model</h3>
                <div className="option-block">
                  <strong>Option A — Subscription Model</strong>
                  <p>Set your monthly rate between $5–$50. Yearly pricing is optional.</p>
                </div>
                <div className="option-block">
                  <strong>Option B — Free Account</strong>
                  <p>Set the subscription price to $0. Your videos may run pre-roll ads, and you earn 60% of ad revenue.</p>
                </div>
              </article>
            </div>
          </section>
        </article>
      </div>
    </LandingLayout>
  );
};

export default HowItWorksPage;
